import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Cookies from "js-cookie";
import {
  getAvailableTenantsSession,
  changeSessionTenant as changeSessionTenantApi,
  getManageTagGroupTenants,
} from "@/api/detechtion-account-api.js";
import {
  abortInFlightRequests,
  isRequestAborted,
  setTenantSwitchInProgress,
} from "@/api/api-utils.js";
import { setActiveTenantId } from "@/api/tenant-change-channel.js";
import { ensureFleetReady } from "@/lib/ensure-fleet-ready.js";
import { useError } from "@/contexts/ErrorContext.jsx";

/**
 * @typedef {Object} NormalizedTenant
 * @property {number} tenantId
 * @property {string} displayName
 * @property {boolean} oneViewEnabled
 * @property {string|null} logoUrl
 * @property {string|null} [logoBase64]
 * @property {string|null} [logoMimeType]
 */

/**
 * @typedef {Object} SessionTenantContextValue
 * @property {NormalizedTenant[]} availableTenants
 * @property {number|null} currentTenantId
 * @property {NormalizedTenant|null} currentTenant
 * @property {boolean} oneViewEnabled
 * @property {boolean} isLoading
 * @property {string|null} error
 * @property {number} tenantRefreshKey - increments when session tenant changes (refetch assets)
 * @property {boolean} canManageTagGroups - whether user has CanManageTagGroups on the current session tenant
 * @property {boolean} isLoadingManageTagGroupTenants - loading state for tag-group manage permission
 * @property {() => Promise<void>} refetch
 * @property {(desiredTenantId: number) => Promise<void>} changeSessionTenant
 */

const SessionTenantContext =
  /** @type {import('react').Context<SessionTenantContextValue|null>} */ (
    createContext(null)
  );

const USERNAME = Cookies.get("DetechtionAuth.Username");

/**
 * @param {import('@/api/detechtion-account-api.js').AvailableTenantsSession} session
 * @param {number|null} expectedTenantId
 */
function resolveCurrentTenant(session, expectedTenantId) {
  if (session.currentTenant) {
    return {
      currentTenant: session.currentTenant,
      currentTenantId: session.currentTenantId,
    };
  }
  if (expectedTenantId != null) {
    const match = session.availableTenants.find(
      (t) => t.tenantId === expectedTenantId
    );
    if (match) {
      return { currentTenant: match, currentTenantId: match.tenantId };
    }
  }
  return {
    currentTenant: session.currentTenant,
    currentTenantId: session.currentTenantId,
  };
}

/**
 * @returns {SessionTenantContextValue}
 */
export function useSessionTenant() {
  const ctx = useContext(SessionTenantContext);
  if (!ctx) {
    throw new Error(
      "useSessionTenant must be used within SessionTenantProvider"
    );
  }
  return ctx;
}

/**
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function SessionTenantProvider({ children }) {
  const { clearAllErrors } = useError();
  const [availableTenants, setAvailableTenants] = useState(
    /** @type {NormalizedTenant[]} */ ([])
  );
  const [currentTenantId, setCurrentTenantId] = useState(
    /** @type {number|null} */ (null)
  );
  const [currentTenant, setCurrentTenant] = useState(
    /** @type {NormalizedTenant|null} */ (null)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [tenantRefreshKey, setTenantRefreshKey] = useState(0);
  const sessionHydratedRef = useRef(false);

  const [canManageTagGroups, setCanManageTagGroups] = useState(false);
  const [isLoadingManageTagGroupTenants, setIsLoadingManageTagGroupTenants] =
    useState(true);

  const loadSession = useCallback(async (expectedTenantId = null) => {
    const showGlobalLoader = !sessionHydratedRef.current;
    if (showGlobalLoader) setIsLoading(true);
    setError(null);
    try {
      let session = await getAvailableTenantsSession();
      let { currentTenant, currentTenantId } = resolveCurrentTenant(
        session,
        expectedTenantId
      );

      if (
        !currentTenant
        && expectedTenantId == null
        && session.availableTenants.length > 0
      ) {
        const defaultId = session.availableTenants[0].tenantId;
        try {
          await changeSessionTenantApi(defaultId);
          session = await getAvailableTenantsSession();
          ({ currentTenant, currentTenantId } = resolveCurrentTenant(
            session,
            defaultId
          ));
        } catch (syncErr) {
          console.warn("Failed to sync default tenant with backend:", syncErr);
        }
      }

      // After an explicit tenant switch, fleet is refetched via tenantRefreshKey.
      if (currentTenantId != null && expectedTenantId == null) {
        await ensureFleetReady(currentTenantId);
      }

      setAvailableTenants(session.availableTenants);
      setCurrentTenantId(currentTenantId);
      setCurrentTenant(currentTenant);
      setActiveTenantId(currentTenantId);
      sessionHydratedRef.current = true;
    } catch (e) {
      console.error("Failed to load session tenants:", e);
      setError("Unable to load tenant session. Please refresh the page.");
      setAvailableTenants([]);
      setCurrentTenantId(null);
      setCurrentTenant(null);
      if (expectedTenantId != null) throw e;
    } finally {
      if (showGlobalLoader) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const changeSessionTenant = useCallback(
    async (desiredTenantId) => {
      setIsLoading(true);
      setError(null);
      clearAllErrors();
      abortInFlightRequests();
      // Pre-emptively mark the desired tenant as active so the mismatch check
      // running on subsequent in-flight requests (e.g. ensureFleetReady) does
      // not false-positive between the server setting the cookie and React
      // committing the new currentTenantId.
      setActiveTenantId(desiredTenantId);
      try {
        await changeSessionTenantApi(desiredTenantId);
        await loadSession(desiredTenantId);
        await ensureFleetReady(desiredTenantId);
        setTenantRefreshKey((k) => k + 1);
      } catch (e) {
        setTenantSwitchInProgress(false);
        if (isRequestAborted(e)) return;
        console.error("Failed to change session tenant:", e);
        setError("Unable to switch tenant. Please try again.");
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [loadSession, clearAllErrors]
  );

  const oneViewEnabled = currentTenant?.oneViewEnabled !== false;

  useEffect(() => {
    if (!USERNAME || !currentTenant || !window.pendo) return;
    try {
      window.pendo.initialize({
        visitor: { id: USERNAME },
        account: {
          id: `${currentTenant.displayName}|${currentTenant.tenantId}`,
        },
      });
    } catch (e) {
      console.warn("Pendo initialize failed:", e);
    }
  }, [currentTenant]);

  useEffect(() => {
    if (currentTenantId == null) {
      setCanManageTagGroups(false);
      setIsLoadingManageTagGroupTenants(false);
      return;
    }

    let cancelled = false;
    setIsLoadingManageTagGroupTenants(true);

    const loadManageTagGroupTenants = async () => {
      try {
        const tenants = await getManageTagGroupTenants();
        if (cancelled) return;
        const list = Array.isArray(tenants) ? tenants : [];
        setCanManageTagGroups(list.some((t) => t.TenantId === currentTenantId));
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch manage tag group tenants:", err);
        setCanManageTagGroups(false);
      } finally {
        if (!cancelled) setIsLoadingManageTagGroupTenants(false);
      }
    };

    loadManageTagGroupTenants();

    return () => {
      cancelled = true;
    };
  }, [currentTenantId, tenantRefreshKey]);

  const value = useMemo(
    () => ({
      availableTenants,
      currentTenantId,
      currentTenant,
      oneViewEnabled,
      isLoading,
      error,
      tenantRefreshKey,
      canManageTagGroups,
      isLoadingManageTagGroupTenants,
      refetch: loadSession,
      changeSessionTenant,
    }),
    [
      availableTenants,
      currentTenantId,
      currentTenant,
      oneViewEnabled,
      isLoading,
      error,
      tenantRefreshKey,
      canManageTagGroups,
      isLoadingManageTagGroupTenants,
      loadSession,
      changeSessionTenant,
    ]
  );

  return (
    <SessionTenantContext.Provider value={value}>
      {children}
    </SessionTenantContext.Provider>
  );
}
